import React, {useEffect, useMemo, useCallback} from 'react';
import {
  Button,
  DataTable,
  DataTableHead,
  DataTableRow,
  Infobox
} from '@flixbus/honeycomb-react';
import {
  IconEditSolid,
  IconDeleteSolid,
  IconDelete,
  Icon,
  IconOfferSolid
} from '@flixbus/honeycomb-icons-react';
import {useCountries} from '@/Pages/Countries/hooks/useCountries';
import ContentLoader from '@/Pages/Common/ContentLoader';
import {useAppDispatch} from '@/Pages/Common/hooks/useAppDispatch';
import {useAppSelector} from '@/Pages/Common/hooks/useAppSelector';
import RegionalCostsDriverConceptTypesPanel from './RegionalCostsDriverConceptTypesPanel';
import {useCountryCostsQueryParams} from '@/Pages/RegionalCosts/hooks/useCountryCostsQueryParams';
import {useToast} from '@/Pages/Common/toasts-container';
import {
  deleteDriverConceptType,
  fetchDriverConceptTypes
} from '@/Pages/RegionalCosts/store/thunks';
import {getScaledNumber} from '@/Pages/Common/Utils';
import {setCountryDriverConceptType} from '@/Pages/RegionalCosts/store/slice';
import {CountryDriverConceptType} from '@/Pages/RegionalCosts/store/types';
import styles from './RegionalCostsDriverConceptTypes.module.scss';
import {confirm} from '@/Pages/Common/Confirm';

const RegionalCostsDriverConceptTypes: React.FC = () => {
  const countryDriverConceptTypes = useAppSelector(
    ({regionalCosts}) => regionalCosts.countryDriverConceptTypes.list
  );
  const dispatch = useAppDispatch();

  const {countries} = useCountries();
  const {countryId} = useCountryCostsQueryParams();
  const country = useMemo(
    () => countries?.find(({id}) => id === countryId) ?? null,
    [countries, countryId]
  );

  const {addToast} = useToast();
  useEffect(() => {
    dispatch(fetchDriverConceptTypes(countryId));
  }, [countryId, dispatch]);

  const deleteDriverConceptTypeAction = useCallback(
    async (driverConceptType: CountryDriverConceptType) => {
      if (
        await confirm({
          PopupIcon: IconDelete,
          title: 'Delete this driver concept type?',
          message: `${driverConceptType.name}`
        })
      ) {
        dispatch(deleteDriverConceptType(driverConceptType, addToast));
      }
    },
    [dispatch, addToast]
  );

  const headers = useMemo(
    () => (
      <DataTableHead>
        <th scope="col">Concept type</th>
        <th scope="col">Description</th>
        <th scope="col">Multiplication factor for working hour adjustment</th>
        <th scope="col">Meals/daily allowance ({country?.currency}/op. day)</th>
        <th scope="col"></th>
        <th scope="col"></th>
      </DataTableHead>
    ),
    [country?.currency]
  );

  const rows = useMemo(
    () =>
      (countryDriverConceptTypes ?? []).map(conceptType => ({
        cells: [
          conceptType.name,
          conceptType.description,
          getScaledNumber(String(conceptType?.multiplicationFactor ?? '')),
          getScaledNumber(String(conceptType.dailyAllowance ?? '')),
          <div key={'edit' + conceptType.id}>
            <Button
              display="square"
              aria-label="Edit Country Driver Concept Type"
              onClick={() => dispatch(setCountryDriverConceptType(conceptType))}
            >
              <Icon InlineIcon={IconEditSolid} aria-hidden="true" />
            </Button>
          </div>,
          <div key={'delete' + conceptType.id}>
            <Button
              display="square"
              aria-label="Delete Country Driver Concept Type"
              appearance={`danger`}
              onClick={() => deleteDriverConceptTypeAction(conceptType)}
            >
              <Icon InlineIcon={IconDeleteSolid} aria-hidden="true" />
            </Button>
          </div>
        ]
      })),
    [countryDriverConceptTypes, deleteDriverConceptTypeAction, dispatch]
  );

  if (!country) {
    return (
      <Infobox appearance="warning">
        <p>
          Select a <strong>country</strong>
        </p>
      </Infobox>
    );
  }

  if (!countryDriverConceptTypes) {
    return <ContentLoader />;
  }

  return (
    <>
      <div className={styles.title}>
        <h3 className={styles.leftSide}>Driver concept types</h3>
        <Button
          disabled={!countryId}
          onClick={() => dispatch(setCountryDriverConceptType(null))}
        >
          <Icon InlineIcon={IconOfferSolid} aria-hidden="true" />
          Add concept
        </Button>
      </div>

      <DataTable headers={headers}>
        {rows.map((row, index) => (
          <DataTableRow key={index}>
            {row.cells.map((cell, index) => (
              <td key={index}>{cell}</td>
            ))}
          </DataTableRow>
        ))}
      </DataTable>

      <RegionalCostsDriverConceptTypesPanel />
    </>
  );
};

export default React.memo(RegionalCostsDriverConceptTypes);
